/**
 * Emeravo — comportements légers du layout "emeravo" (accueil, produit, collection) et de l'en-tête.
 * S'appuie sur les composants Dawn (pubsub.js, product-info.js, product-form.js) :
 * aucune logique panier/variante n'est dupliquée ici.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onVariantChange = (callback) => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return subscribe(PUB_SUB_EVENTS.variantChange, callback);
    }
    return () => {};
  };

  /* ------------------------------------------------------------------
   * Template alternatif (?view=…) : conserve le paramètre lors des
   * changements de variante pour que la Section Rendering API et l'URL
   * restent sur le template produit (utile en prévisualisation).
   * ------------------------------------------------------------------ */
  const initialView = new URLSearchParams(window.location.search).get('view');
  customElements.whenDefined('product-info').then(() => {
    const proto = customElements.get('product-info').prototype;
    if (proto.__empPatched) return;
    proto.__empPatched = true;

    const buildRequestUrl = proto.buildRequestUrlWithParams;
    proto.buildRequestUrlWithParams = function (url, optionValues, shouldFetchFullPage) {
      const requestUrl = buildRequestUrl.call(this, url, optionValues, shouldFetchFullPage);
      const view = this.dataset.empView;
      if (!view) return requestUrl;
      return `${requestUrl}${requestUrl.includes('?') ? '&' : '?'}view=${encodeURIComponent(view)}`;
    };

    const updateURL = proto.updateURL;
    proto.updateURL = function (url, variantId) {
      updateURL.call(this, url, variantId);
      if (!initialView || !this.dataset.empView || this.dataset.updateUrl === 'false') return;
      const next = new URL(window.location.href);
      next.searchParams.set('view', initialView);
      window.history.replaceState({}, '', next);
    };
  });

  /* ------------------------------------------------------------------
   * Galerie produit : scroll-snap natif + points / miniatures / flèches.
   * ------------------------------------------------------------------ */
  class EmpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.emp-gallery__track');
      this.slides = this.track ? Array.from(this.track.querySelectorAll('.emp-gallery__slide')) : [];
      if (this.slides.length < 2) return;

      this.index = 0;
      this.navButtons = Array.from(this.querySelectorAll('[data-index]'));
      this.arrows = Array.from(this.querySelectorAll('[data-dir]'));
      this.counter = this.querySelector('[data-current]');

      this.navButtons.forEach((button) =>
        button.addEventListener('click', () => this.goTo(Number(button.dataset.index)))
      );
      this.arrows.forEach((arrow) =>
        arrow.addEventListener('click', () => this.goTo(this.index + Number(arrow.dataset.dir)))
      );
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
      });

      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.syncFromScroll();
          });
        },
        { passive: true }
      );

      this.unsubscribe = onVariantChange(({ data }) => {
        if (data.sectionId !== this.dataset.section) return;
        const mediaId = data.variant?.featured_media?.id;
        if (!mediaId) return;
        const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (target > -1) this.goTo(target);
      });

      this.render();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    get step() {
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft || this.track.clientWidth;
    }

    syncFromScroll() {
      const next = Math.round(this.track.scrollLeft / this.step);
      if (next !== this.index) {
        this.index = Math.max(0, Math.min(next, this.slides.length - 1));
        this.render();
      }
    }

    goTo(index) {
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({
        left: this.slides[target].offsetLeft - this.slides[0].offsetLeft,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.index = target;
      this.render();
    }

    render() {
      this.navButtons.forEach((button) => {
        const active = Number(button.dataset.index) === this.index;
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(this.index + 1);
      this.arrows.forEach((arrow) => {
        const dir = Number(arrow.dataset.dir);
        arrow.disabled = (dir < 0 && this.index === 0) || (dir > 0 && this.index === this.slides.length - 1);
      });
      this.slides.forEach((slide, i) => {
        if (i === this.index) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }
  }

  if (!customElements.get('emp-gallery')) customElements.define('emp-gallery', EmpGallery);

  /* ------------------------------------------------------------------
   * Buy box : prix partagé, libellé du CTA, barre d'achat mobile.
   * ------------------------------------------------------------------ */
  const readState = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-emp-state]')?.textContent || 'null');
    } catch {
      return null;
    }
  };

  const initBuyBox = (hero) => {
    if (hero.dataset.empReady) return;
    hero.dataset.empReady = 'true';
    const sectionId = hero.dataset.section;
    const getMainButton = () => document.getElementById(`ProductSubmitButton-${sectionId}`);
    const bar = hero.querySelector('[data-emp-sticky]');
    const stickyButton = bar?.querySelector('[data-emp-sticky-add]');

    // Réapplique le libellé personnalisé (Dawn remet la traduction par défaut après un changement de variante).
    const syncButtons = () => {
      const main = getMainButton();
      if (!main) return;
      const label = main.querySelector('span');
      const disabled = main.hasAttribute('disabled');
      const addLabel = main.dataset.addLabel;
      const defaultAdd = window.variantStrings?.addToCart;
      if (label) {
        // Dawn réécrit le libellé avec les traductions de la langue de la boutique : on réapplique les nôtres.
        const current = label.textContent.trim();
        const replacements = {
          [defaultAdd]: addLabel,
          [window.variantStrings?.soldOut]: main.dataset.soldOutLabel,
          [window.variantStrings?.unavailable]: main.dataset.unavailableLabel,
        };
        const next = replacements[current];
        if (next && next !== current) label.textContent = next;
      }

      if (!stickyButton) return;
      const mainText = label ? label.textContent.trim() : '';
      const isAddText = mainText === addLabel || mainText === defaultAdd;
      stickyButton.disabled = disabled;
      stickyButton.textContent = !disabled || isAddText ? stickyButton.dataset.addLabel : mainText || stickyButton.dataset.soldOutLabel;
      stickyButton.setAttribute('aria-busy', String(main.classList.contains('loading')));
    };

    const observeMainButton = () => {
      const main = getMainButton();
      if (!main) return;
      new MutationObserver(syncButtons).observe(main, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'aria-disabled'],
      });
      syncButtons();
    };
    observeMainButton();

    onVariantChange(({ data }) => {
      if (data.sectionId !== sectionId) return;
      const state = readState(data.html);
      if (state) {
        document.querySelectorAll('[data-emp-price]').forEach((el) => (el.textContent = state.price));
        hero.querySelectorAll('[data-emp-variant-title]').forEach((el) => (el.textContent = state.title));
        const current = hero.querySelector('[data-emp-state]');
        if (current) current.textContent = JSON.stringify(state);
      }
      const stock = hero.querySelector(`#Inventory-${sectionId}`);
      const nextStock = data.html.getElementById(`Inventory-${sectionId}`);
      if (stock && nextStock) stock.className = nextStock.className;
      syncButtons();
    });

    if (!bar || !stickyButton) return;

    stickyButton.addEventListener('click', () => {
      const main = getMainButton();
      if (!main || main.hasAttribute('disabled') || main.getAttribute('aria-disabled') === 'true') return;
      main.click();
    });

    const mobile = window.matchMedia('(max-width: 989px)');
    const form = hero.querySelector('.emp-form');
    const finalCta = document.querySelector('[data-emp-final]');
    let ctaPassed = false;
    let finalVisible = false;

    const update = () => {
      const show = mobile.matches && ctaPassed && !finalVisible;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      document.body.classList.toggle('emp-has-sticky', mobile.matches);
    };

    if ('IntersectionObserver' in window && form) {
      new IntersectionObserver(([entry]) => {
        ctaPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(form);

      if (finalCta) {
        new IntersectionObserver(([entry]) => {
          finalVisible = entry.isIntersecting;
          update();
        }).observe(finalCta);
      }
    }
    mobile.addEventListener?.('change', update);
    update();
  };


  /* ------------------------------------------------------------------
   * Sélecteur d'offres (1, 2 ou 3 produits) — section em-main-product.
   * - Couleur 1 = sélecteur Dawn (champ « id » du formulaire, galerie, URL) : inchangé.
   * - Couleurs 2 et 3 : champs items[n][id] / items[n][quantity] ajoutés au MÊME formulaire ;
   *   Shopify ajoute alors « id » + items[] en un seul appel (vraies variantes, quantités cumulées).
   * - Prix affiché = somme des prix réels des variantes choisies, ou prix par palier (data.tiers)
   *   seulement si la section l'autorise (remises automatiques Shopify actives).
   * - La barre d'achat mobile clique le bouton principal : elle envoie exactement cette sélection.
   * ------------------------------------------------------------------ */
  const formatMoney = (cents, format) => {
    const value = (Number(cents) || 0) / 100;
    const fmt = (decimals, thousands, decimal) => {
      const [int, dec] = value.toFixed(decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? grouped + decimal + dec : grouped;
    };
    const formats = {
      amount: () => fmt(2, ',', '.'),
      amount_no_decimals: () => fmt(0, ',', '.'),
      amount_with_comma_separator: () => fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(0, '.', ','),
      amount_with_space_separator: () => fmt(2, ' ', ','),
      amount_with_period_and_space_separator: () => fmt(2, ' ', '.'),
      amount_with_apostrophe_separator: () => fmt(2, "'", '.'),
    };
    return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (formats[key] || formats.amount)());
  };

  const initBundle = (hero) => {
    const offers = hero.querySelector('[data-emp-offers]');
    const dataNode = hero.querySelector('[data-emp-bundle-data]');
    if (!offers || !dataNode || hero.dataset.empBundleReady) return;
    hero.dataset.empBundleReady = 'true';

    let data;
    try {
      data = JSON.parse(dataNode.textContent);
    } catch {
      return;
    }
    const variants = new Map(data.variants.map((variant) => [String(variant.id), variant]));
    const sectionId = hero.dataset.section;
    const purchase = hero.querySelector('.emp-purchase');
    const rows = Array.from(hero.querySelectorAll('[data-emp-extra-row]'));
    const getForm = () => document.getElementById(`product-form-${sectionId}`);
    const mainId = () => String(getForm()?.querySelector('input[name="id"]')?.value || '');
    const offer = () => Number(offers.querySelector('[data-emp-offer-input]:checked')?.value || 1);
    const rowId = (row) => String(row.querySelector('[data-emp-extra-input]:checked')?.value || '');
    const price = (id) => variants.get(String(id))?.price ?? 0;

    // Chaque sélecteur (couleur 1 = sélecteur Dawn, couleurs 2 / 3 = groupes radio distincts)
    // conserve son propre état : aucune couleur n'est jamais recopiée d'un sélecteur à l'autre.

    const selection = () => {
      const count = offer();
      const ids = [mainId()];
      rows.forEach((row) => {
        if (Number(row.dataset.empExtraRow) <= count) ids.push(rowId(row));
      });
      return ids.filter(Boolean);
    };

    const render = () => {
      const count = offer();
      purchase.dataset.offer = String(count);

      rows.forEach((row) => {
        row.hidden = Number(row.dataset.empExtraRow) > count;
        const name = row.querySelector('[data-emp-extra-name]');
        const variant = variants.get(rowId(row));
        if (name && variant) name.textContent = variant.title;
      });

      const ids = selection();
      const box = getForm()?.querySelector('[data-emp-bundle-inputs]');
      if (box) {
        box.innerHTML = ids
          .slice(1)
          .map(
            (id, index) =>
              `<input type="hidden" name="items[${index}][id]" value="${id}"><input type="hidden" name="items[${index}][quantity]" value="1">`
          )
          .join('');
      }

      // Prix de chaque offre (somme des variantes qui seraient ajoutées) et prix total sélectionné.
      offers.querySelectorAll('[data-emp-offer-price]').forEach((el) => {
        const size = Number(el.dataset.empOfferPrice);
        let sum = price(mainId());
        rows.forEach((row) => {
          if (Number(row.dataset.empExtraRow) <= size) sum += price(rowId(row)) || price(mainId());
        });
        const tier = data.tiers?.[size];
        el.textContent = formatMoney(tier ?? sum, data.moneyFormat);
        // Prix de référence barré : somme des prix des variantes, seulement si le palier est inférieur.
        const compare = offers.querySelector(`[data-emp-offer-compare="${size}"]`);
        if (compare) {
          const showCompare = tier != null && tier < sum;
          compare.hidden = !showCompare;
          const value = compare.querySelector('[data-emp-offer-compare-value]');
          if (value && showCompare) value.textContent = formatMoney(sum, data.moneyFormat);
        }
      });
      const tierTotal = data.tiers?.[count];
      const total = tierTotal ?? ids.reduce((sum, id) => sum + price(id), 0);
      hero.querySelectorAll('.emp-price__current').forEach((el) => (el.textContent = formatMoney(total, data.moneyFormat)));
      document.querySelectorAll('[data-emp-price]').forEach((el) => (el.textContent = formatMoney(total, data.moneyFormat)));

      const titles = ids.map((id) => variants.get(String(id))?.title).filter(Boolean);
      hero.querySelectorAll('[data-emp-variant-title]').forEach((el) => {
        el.textContent = count > 1 ? `${count} ${data.plural} · ${titles.join(', ')}` : titles[0] || '';
      });
      window.__empBundleIds = ids;
    };

    offers.addEventListener('change', render);
    hero.querySelector('[data-emp-extra]')?.addEventListener('change', render);
    onVariantChange(({ data: payload }) => {
      if (payload.sectionId !== sectionId) return;
      render();
    });
    render();
  };

  /* Notification panier Dawn : affiche aussi les produits 2 et 3 d'une offre multi-couleurs
     (par défaut, Dawn n'affiche que la ligne du champ « id »). */
  customElements.whenDefined('cart-notification').then(() => {
    const proto = customElements.get('cart-notification').prototype;
    if (proto.__empPatched) return;
    proto.__empPatched = true;
    const renderContents = proto.renderContents;
    proto.renderContents = function (parsedState) {
      renderContents.call(this, parsedState);
      const ids = window.__empBundleIds;
      const html = parsedState?.sections?.['cart-notification-product'];
      if (!Array.isArray(ids) || ids.length < 2 || !html) return;
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const target = document.getElementById('cart-notification-product');
        const seen = new Set([String(parsedState.key)]);
        const extra = [];
        [...new Set(ids.map(String))].forEach((id) => {
          doc.querySelectorAll(`[id^="cart-notification-product-${id}:"]`).forEach((el) => {
            const key = el.id.replace('cart-notification-product-', '');
            if (seen.has(key)) return;
            seen.add(key);
            extra.push(`<div class="cart-notification-product emp-notification-extra">${el.innerHTML}</div>`);
          });
        });
        if (target && extra.length) target.insertAdjacentHTML('beforeend', extra.join(''));
      } catch {
        /* affichage informatif uniquement : l'ajout au panier a déjà réussi */
      }
    };
  });


  /* ------------------------------------------------------------------
   * « Revenir à ma sélection » — retour vers la dernière fiche produit consultée.
   * - Fiche produit : mémorise UNIQUEMENT le chemin interne /products/<handle> (sans paramètres,
   *   sans donnée personnelle) dans sessionStorage (onglet courant uniquement).
   * - Accueil : les liens [data-em-return] et les blocs [data-em-return-block] (masqués par défaut)
   *   ne s'affichent que si ce chemin est valide ET que le produit existe encore (/products/<handle>.js).
   *   Aucune redirection, aucun popup.
   * - Panier : les liens [data-em-continue] (« Continuer les achats », destination de secours : accueil)
   *   pointent vers cette même fiche produit lorsqu'elle est valide. Jamais vers un catalogue.
   * - Stockage indisponible / valeur invalide : rien n'est affiché, la valeur invalide est effacée.
   * ------------------------------------------------------------------ */
  const RETURN_KEY = 'emeravo:last-product';
  const PRODUCT_PATH = /^(\/[a-z]{2}(?:-[a-z]{2})?)?\/products\/([^/?#\s\\]+)\/?$/i;

  const normalizeProductPath = (pathname) => {
    if (typeof pathname !== 'string' || !pathname.startsWith('/') || pathname.startsWith('//')) return null;
    // /collections/<x>/products/<handle> → /products/<handle>
    const path = pathname.replace(/^((?:\/[a-z]{2}(?:-[a-z]{2})?)?)\/collections\/[^/]+(\/products\/)/i, '$1$2');
    const match = path.match(PRODUCT_PATH);
    return match ? `${match[1] || ''}/products/${match[2]}` : null;
  };

  const storage = {
    get() {
      try {
        return window.sessionStorage.getItem(RETURN_KEY);
      } catch {
        return null;
      }
    },
    set(value) {
      try {
        window.sessionStorage.setItem(RETURN_KEY, value);
      } catch {
        /* stockage indisponible (navigation privée stricte…) : fonctionnalité simplement inactive */
      }
    },
    clear() {
      try {
        window.sessionStorage.removeItem(RETURN_KEY);
      } catch {
        /* ignoré */
      }
    },
  };

  const rememberProduct = () => {
    const template = document.querySelector('main[data-template]')?.dataset.template || '';
    if (!template.startsWith('product')) return;
    const path = normalizeProductPath(window.location.pathname);
    if (path) storage.set(path);
  };

  const initReturnLinks = () => {
    const links = document.querySelectorAll('[data-em-return]');
    const blocks = document.querySelectorAll('[data-em-return-block]');
    const continues = document.querySelectorAll('[data-em-continue]');
    if (!links.length && !blocks.length && !continues.length) return;
    const raw = storage.get();
    if (!raw) return;
    const path = normalizeProductPath(raw);
    if (!path) {
      storage.clear();
      return;
    }
    fetch(`${path}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product || !product.handle) {
          storage.clear();
          return;
        }
        links.forEach((link) => {
          link.href = path;
          link.hidden = false;
        });
        blocks.forEach((block) => {
          block.hidden = false;
        });
        continues.forEach((link) => {
          link.href = path;
        });
      })
      .catch(() => {
        /* réseau indisponible : le bouton reste masqué */
      });
  };

  /* ------------------------------------------------------------------
   * Liens internes (CTA final → buy box) : défilement + focus accessible.
   * ------------------------------------------------------------------ */
  const initScrollLinks = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-emp-scroll]');
      if (!link) return;
      const id = decodeURIComponent(link.hash.slice(1));
      const target = id && document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  };

  /* ------------------------------------------------------------------
   * Vidéos de section : lecture uniquement à l'écran, jamais si
   * l'utilisateur préfère réduire les animations.
   * ------------------------------------------------------------------ */
  const initAutoplayVideos = () => {
    const videos = document.querySelectorAll('[data-em-autoplay] video');
    if (!videos.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      videos.forEach((video) => (video.controls = true));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) target.play().catch(() => (target.controls = true));
          else target.pause();
        }),
      { rootMargin: '100px 0px' }
    );
    videos.forEach((video) => observer.observe(video));
  };

  /* Décalage du header collant (ancres / buy box sticky). */
  const syncHeaderOffset = () => {
    const header = document.querySelector('.em-header-section, .section-header');
    if (header) document.body.style.setProperty('--em-header-offset', `${header.offsetHeight}px`);
  };

  /* ------------------------------------------------------------------
   * Menu mobile de l'en-tête Emeravo (<details> natif) :
   * fermeture au clic sur un lien (ancres) et à la touche Échap.
   * ------------------------------------------------------------------ */
  const initHeaderMenu = () => {
    document.querySelectorAll('[data-em-menu]').forEach((menu) => {
      if (menu.dataset.vReady) return;
      menu.dataset.vReady = 'true';
      const summary = menu.querySelector('summary');
      summary?.setAttribute('aria-expanded', 'false');
      menu.addEventListener('toggle', () => summary?.setAttribute('aria-expanded', String(menu.open)));
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => (menu.open = false)));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !menu.open) return;
        menu.open = false;
        summary?.focus();
      });
      document.addEventListener('click', (event) => {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      });
    });
  };

  /* ------------------------------------------------------------------
   * Apparition discrète au scroll (accueil, page produit, page « Nos produits »).
   * - Les éléments sont choisis ici (liste unique) ; [data-em-reveal] = « révéler ses enfants ».
   * - Le masquage n'est posé QUE par ce script, et seulement sous la ligne de flottaison :
   *   sans JS, en cas d'erreur ou si le mouvement est réduit, tout reste visible.
   * - Une seule fois par élément ; à la fin, les classes sont retirées (état d'origine intact).
   * - Exclus : hero, galerie / buy box produit, widget Judge.me, header, footer.
   * - Opacité + translation verticale 20 px, 600 ms, décalage de 90 ms entre éléments voisins (CSS).
   * ------------------------------------------------------------------ */
  const REVEAL_ITEMS = [
    '[data-em-reveal] > *',
    '.em-section__head > *',
    '.emp-feature__media',
    '.emp-feature__content > *',
    '.emh-cases__scroller',
    '.emh-essentials__media',
    '.emh-variants__scroller',
    '.emh-variants__cta',
    '.em-grid > .em-card',
    '.emh-reviews__cta',
    '.em-accordion__item',
    '.emp-faq__more',
    '.emh-final__media',
    '.emp-card',
    '.emp-steps__item',
    '.emp-compare__wrap',
    '.emp-reviews__summary',
    '.emp-final__inner > *',
  ].join(',');
  const REVEAL_EXCLUDED = '.emh-hero, .emp-section--hero, .emp-reviews__app, [class*="jdgm"]';
  const REVEAL_STAGGER = 90; // ms entre éléments apparaissant ensemble
  const REVEAL_MAX_STEPS = 4;

  const initReveal = () => {
    const main = document.getElementById('MainContent');
    if (!main || !main.querySelector('.emh-hero, .em-brand-hero, .emp-section--hero, .em-grid-section')) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

    const candidates = Array.from(main.querySelectorAll(REVEAL_ITEMS)).filter(
      (el) => !el.closest(REVEAL_EXCLUDED) && !el.classList.contains('em-reveal')
    );
    // Un élément dont un parent est déjà animé ne l'est pas une seconde fois.
    const items = candidates.filter((el) => !candidates.some((other) => other !== el && other.contains(el)));
    // Contenu déjà à l'écran au chargement : jamais masqué.
    const below = items.filter((el) => el.getBoundingClientRect().top >= window.innerHeight);
    if (!below.length) return;

    const cleanup = (el) => {
      el.classList.remove('em-reveal', 'is-revealed');
      el.style.removeProperty('--em-reveal-delay');
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left)
          .forEach(({ target }, index) => {
            observer.unobserve(target);
            const delay = Math.min(index, REVEAL_MAX_STEPS) * REVEAL_STAGGER;
            if (delay) target.style.setProperty('--em-reveal-delay', `${delay}ms`);
            target.classList.add('is-revealed');
            const done = () => {
              target.removeEventListener('transitionend', onEnd);
              cleanup(target);
            };
            const onEnd = (event) => event.target === target && done();
            target.addEventListener('transitionend', onEnd);
            setTimeout(done, delay + 900);
          });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );

    below.forEach((el) => {
      el.classList.add('em-reveal');
      observer.observe(el);
    });
    document.documentElement.classList.add('em-reveal-ready');

    const revealAll = () => document.querySelectorAll('.em-reveal').forEach(cleanup);
    window.addEventListener('beforeprint', revealAll);
    reducedMotion.addEventListener?.('change', revealAll);
  };

  /* ------------------------------------------------------------------
   * Avis Judge.me (section emp-reviews)
   * 1. Répartition des notes : lue dans les données que Judge.me injecte sur la page
   *    (jdgm.data.reviewWidget[id].histogram). Rien n'est calculé ni codé en dur ;
   *    sans données, la liste reste masquée.
   * 2. Avis importés sans commentaire : leur texte est seulement le nom du produit.
   *    Ce texte (égalité stricte) est masqué à l'affichage ; note, auteur et photos restent visibles.
   *    Les données Judge.me ne sont pas modifiées.
   * ------------------------------------------------------------------ */
  const initReviewsHistogram = () => {
    document.querySelectorAll('[data-emp-histogram]').forEach((summary) => {
      const list = summary.querySelector('[data-emp-histogram-list]');
      const data = window.jdgm?.data?.reviewWidget?.[summary.dataset.productId];
      const rows = Array.isArray(data?.histogram) ? data.histogram : null;
      if (!list || !rows || !rows.length || list.childElementCount) return;
      const label = list.dataset.countLabel || 'avis';
      rows
        .slice()
        .sort((a, b) => Number(b.rating) - Number(a.rating))
        .forEach((row) => {
          const li = document.createElement('li');
          li.className = 'emp-reviews__bar';
          li.setAttribute('aria-label', `${row.rating} ★ : ${row.frequency} ${label}`);
          li.innerHTML =
            `<span class="emp-reviews__bar-label" aria-hidden="true">${Number(row.rating)}<svg class="emp-reviews__bar-star" viewBox="0 0 24 24" focusable="false"><path d="M12 2.6l2.92 5.92 6.53.95-4.73 4.61 1.12 6.5L12 17.51l-5.84 3.07 1.12-6.5-4.73-4.61 6.53-.95z"/></svg></span>` +
            `<span class="emp-reviews__bar-track" aria-hidden="true"><span class="emp-reviews__bar-fill" style="width:${Math.max(0, Math.min(100, Number(row.percentage) || 0))}%"></span></span>` +
            `<span class="emp-reviews__bar-count" aria-hidden="true">${Number(row.frequency) || 0}</span>`;
          list.appendChild(li);
        });
      list.hidden = false;
      summary.classList.add('has-histogram');
    });
  };

  const initReviewPlaceholders = () => {
    document.querySelectorAll('[data-emp-hide-placeholder]').forEach((container) => {
      if (container.dataset.empPlaceholderReady) return;
      container.dataset.empPlaceholderReady = 'true';
      const placeholder = container.dataset.empHidePlaceholder.trim();
      if (!placeholder) return;
      const apply = () => {
        container.querySelectorAll('.jdgm-review-content__body-content').forEach((body) => {
          const isPlaceholder = body.textContent.trim() === placeholder;
          const wrapper = body.closest('.jm-review-body-wrapper') || body;
          wrapper.classList.toggle('emp-review-placeholder', isPlaceholder);
        });
      };
      apply();
      let scheduled = false;
      new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          apply();
        });
      }).observe(container, { childList: true, subtree: true });
    });
  };

  /* Offres « lots » : chaque offre est une vraie variante Shopify (prix facturé = prix de la variante). */
  const initPack = (hero) => {
    const offers = hero.querySelector('[data-emp-offers][data-emp-pack]');
    if (!offers || hero.dataset.empPackReady) return;
    hero.dataset.empPackReady = 'true';
    const sectionId = hero.dataset.section;
    const render = () => {
      const input = offers.querySelector('[data-emp-offer-input]:checked');
      if (!input) return;
      const id = input.dataset.empPackVariant;
      hero.querySelector('.emp-purchase').dataset.offer = input.value;
      document.querySelectorAll(`#product-form-${sectionId} input[name="id"], #product-form-installment-${sectionId} input[name="id"]`).forEach((field) => {
        field.value = id;
        field.disabled = false;
      });
      hero.querySelectorAll('.emp-price__current').forEach((el) => (el.textContent = input.dataset.empPackPrice));
      hero.querySelectorAll('.emp-price__compare').forEach((el) => el.remove());
      document.querySelectorAll('[data-emp-price]').forEach((el) => (el.textContent = input.dataset.empPackPrice));
    };
    offers.addEventListener('change', render);
    render();
  };

  const init = () => {
    document.querySelectorAll('product-info.emp-hero').forEach(initBuyBox);
    document.querySelectorAll('product-info.emp-hero').forEach(initBundle);
    document.querySelectorAll('product-info.emp-hero').forEach(initPack);
    rememberProduct();
    initReturnLinks();
    initReviewsHistogram();
    window.addEventListener('load', initReviewsHistogram, { once: true });
    initReviewPlaceholders();
    initScrollLinks();
    initAutoplayVideos();
    initHeaderMenu();
    initReveal();
    syncHeaderOffset();
    window.addEventListener('resize', syncHeaderOffset, { passive: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('product-info.emp-hero').forEach(initBuyBox);
    document.querySelectorAll('product-info.emp-hero').forEach(initBundle);
    document.querySelectorAll('product-info.emp-hero').forEach(initPack);
    initAutoplayVideos();
    initHeaderMenu();
    document.querySelectorAll('.em-reveal').forEach((item) => item.classList.remove('em-reveal', 'is-revealed'));
  });
})();
